import type { LeadRecord } from '@/lib/auth-client';

export const LEAD_EXPORT_HEADERS = [
  'Nom et prénom',
  'Email',
  'Téléphone',
  'Région',
  'Source',
  'Vendeur',
  'Résultat',
  'Lot',
  'Date',
] as const;

export function formatFrenchPhone(phone: string | null | undefined) {
  if (!phone) {
    return '';
  }

  const digits = phone.replace(/\D/g, '');
  if (!digits) {
    return '';
  }

  let national = digits;
  if (national.startsWith('33') && national.length >= 11) {
    national = national.slice(2);
  } else if (national.startsWith('0') && national.length === 10) {
    national = national.slice(1);
  }

  if (national.length !== 9) {
    return national.startsWith('33') ? `+${national}` : `+33 ${national}`;
  }

  return `+33 ${[
    national.slice(0, 1),
    national.slice(1, 3),
    national.slice(3, 5),
    national.slice(5, 7),
    national.slice(7, 9),
  ].join(' ')}`;
}

export function formatLeadExportDate(value: string) {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export function leadRecordToExportRow(lead: LeadRecord) {
  return [
    `${lead.lastName} ${lead.firstName}`.trim(),
    lead.email,
    formatFrenchPhone(lead.phone),
    lead.regionName || '',
    lead.salonName || '',
    lead.sellerName || '',
    lead.win ? 'Gagné' : 'Perdu',
    lead.prizeName || '-',
    formatLeadExportDate(lead.createdAt),
  ];
}

function escapeExcelCell(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function excelColumnName(columnIndex: number) {
  let current = columnIndex + 1;
  let label = '';

  while (current > 0) {
    const remainder = (current - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    current = Math.floor((current - 1) / 26);
  }

  return label;
}

function createWorksheetXml(headers: string[], rows: string[][]) {
  const allRows = [headers, ...rows];
  const worksheetRows = allRows
    .map((row, rowIndex) => {
      const cells = row
        .map((cell, columnIndex) => {
          const reference = `${excelColumnName(columnIndex)}${rowIndex + 1}`;
          const styleAttribute = rowIndex === 0 ? ' s="1"' : '';
          return `<c r="${reference}" t="inlineStr"${styleAttribute}><is><t>${escapeExcelCell(cell)}</t></is></c>`;
        })
        .join('');

      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetData>${worksheetRows}</sheetData>
</worksheet>`;
}

function createWorkbookXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook
  xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
>
  <sheets>
    <sheet name="Leads" sheetId="1" r:id="rId1" />
  </sheets>
</workbook>`;
}

function createWorkbookRelsXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship
    Id="rId1"
    Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet"
    Target="worksheets/sheet1.xml"
  />
  <Relationship
    Id="rId2"
    Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles"
    Target="styles.xml"
  />
</Relationships>`;
}

function createRootRelsXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship
    Id="rId1"
    Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument"
    Target="xl/workbook.xml"
  />
</Relationships>`;
}

function createContentTypesXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml" />
  <Default Extension="xml" ContentType="application/xml" />
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml" />
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml" />
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml" />
</Types>`;
}

function createStylesXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font>
      <sz val="11" />
      <name val="Aptos" />
    </font>
    <font>
      <b />
      <sz val="11" />
      <name val="Aptos" />
    </font>
  </fonts>
  <fills count="2">
    <fill><patternFill patternType="none" /></fill>
    <fill>
      <patternFill patternType="solid">
        <fgColor rgb="FFEAF1FF" />
        <bgColor indexed="64" />
      </patternFill>
    </fill>
  </fills>
  <borders count="1">
    <border>
      <left />
      <right />
      <top />
      <bottom />
      <diagonal />
    </border>
  </borders>
  <cellStyleXfs count="1">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" />
  </cellStyleXfs>
  <cellXfs count="2">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" />
    <xf numFmtId="0" fontId="1" fillId="1" borderId="0" xfId="0" applyFont="1" applyFill="1" />
  </cellXfs>
</styleSheet>`;
}

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);

  for (let index = 0; index < 256; index += 1) {
    let crc = index;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc & 1) === 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    }
    table[index] = crc >>> 0;
  }

  return table;
})();

function computeCrc32(bytes: Uint8Array) {
  let crc = 0xffffffff;

  for (const value of bytes) {
    crc = CRC32_TABLE[(crc ^ value) & 0xff]! ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
}

function encodeUtf8(value: string) {
  return new TextEncoder().encode(value);
}

function uint16(value: number) {
  const output = new Uint8Array(2);
  const view = new DataView(output.buffer);
  view.setUint16(0, value, true);
  return output;
}

function uint32(value: number) {
  const output = new Uint8Array(4);
  const view = new DataView(output.buffer);
  view.setUint32(0, value, true);
  return output;
}

function concatBytes(parts: Uint8Array[]) {
  const totalLength = parts.reduce((sum, part) => sum + part.length, 0);
  const output = new Uint8Array(totalLength);
  let offset = 0;

  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }

  return output;
}

function createZip(entries: Array<{ data: string; name: string }>) {
  const localChunks: Uint8Array[] = [];
  const centralChunks: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = encodeUtf8(entry.name);
    const dataBytes = encodeUtf8(entry.data);
    const crc32 = computeCrc32(dataBytes);

    const localHeader = concatBytes([
      uint32(0x04034b50),
      uint16(20),
      uint16(0),
      uint16(0),
      uint16(0),
      uint16(0),
      uint32(crc32),
      uint32(dataBytes.length),
      uint32(dataBytes.length),
      uint16(nameBytes.length),
      uint16(0),
      nameBytes,
      dataBytes,
    ]);

    localChunks.push(localHeader);

    const centralHeader = concatBytes([
      uint32(0x02014b50),
      uint16(20),
      uint16(20),
      uint16(0),
      uint16(0),
      uint16(0),
      uint16(0),
      uint32(crc32),
      uint32(dataBytes.length),
      uint32(dataBytes.length),
      uint16(nameBytes.length),
      uint16(0),
      uint16(0),
      uint16(0),
      uint16(0),
      uint32(0),
      uint32(offset),
      nameBytes,
    ]);

    centralChunks.push(centralHeader);
    offset += localHeader.length;
  }

  const centralDirectory = concatBytes(centralChunks);
  const localDirectory = concatBytes(localChunks);
  const endOfCentralDirectory = concatBytes([
    uint32(0x06054b50),
    uint16(0),
    uint16(0),
    uint16(entries.length),
    uint16(entries.length),
    uint32(centralDirectory.length),
    uint32(localDirectory.length),
    uint16(0),
  ]);

  return concatBytes([localDirectory, centralDirectory, endOfCentralDirectory]);
}

function buildExcelWorkbook(headers: string[], rows: string[][]) {
  return createZip([
    { name: '[Content_Types].xml', data: createContentTypesXml() },
    { name: '_rels/.rels', data: createRootRelsXml() },
    { name: 'xl/workbook.xml', data: createWorkbookXml() },
    { name: 'xl/_rels/workbook.xml.rels', data: createWorkbookRelsXml() },
    { name: 'xl/styles.xml', data: createStylesXml() },
    { name: 'xl/worksheets/sheet1.xml', data: createWorksheetXml(headers, rows) },
  ]);
}

export function downloadLeadsAsXlsx(leads: LeadRecord[], filename: string) {
  const headers = [...LEAD_EXPORT_HEADERS];
  const rows = leads.map((lead) => leadRecordToExportRow(lead));
  const excelContent = buildExcelWorkbook(headers, rows);
  const blob = new Blob([excelContent], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
